/**
 * Utility functions for handling antimeridian (International Date Line) crossing
 * when drawing routes on maps.
 *
 * The antimeridian is at 180° longitude. When a route crosses from e.g. -178° to +178°,
 * map libraries will draw the line "the long way" through Europe/Asia instead of
 * the short way across the Pacific.
 *
 * Solution: Split routes into segments at the antimeridian.
 */

export interface Coordinate {
  lat: number;
  lon: number;
}

/**
 * Detects if a route segment crosses the antimeridian.
 * A crossing is detected when the longitude difference exceeds 180°.
 */
export function crossesAntimeridian(lon1: number, lon2: number): boolean {
  return Math.abs(lon2 - lon1) > 180;
}

/**
 * Calculates the latitude where a line segment crosses the antimeridian.
 * Uses linear interpolation between the two points.
 */
function calculateCrossingLatitude(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  // Normalize longitudes for the calculation
  let normalizedLon1 = lon1;
  let normalizedLon2 = lon2;

  if (lon1 > 0 && lon2 < 0) {
    normalizedLon2 = lon2 + 360;
  } else if (lon1 < 0 && lon2 > 0) {
    normalizedLon1 = lon1 + 360;
  }

  // Calculate the fraction of the distance to reach 180°
  const normalizedTarget = lon1 > 0 ? 180 : 180;

  const fraction = (normalizedTarget - normalizedLon1) / (normalizedLon2 - normalizedLon1);

  // Linear interpolation for latitude
  return lat1 + fraction * (lat2 - lat1);
}

/**
 * Splits a route into multiple segments at antimeridian crossings.
 * Each segment can be drawn as a separate line without crossing artifacts.
 *
 * @param coordinates Array of coordinates in the route
 * @returns Array of coordinate arrays, each representing a segment
 */
export function splitAtAntimeridian(coordinates: Coordinate[]): Coordinate[][] {
  if (coordinates.length < 2) {
    return [coordinates];
  }

  const segments: Coordinate[][] = [];
  let currentSegment: Coordinate[] = [coordinates[0]];

  for (let i = 1; i < coordinates.length; i++) {
    const prev = coordinates[i - 1];
    const curr = coordinates[i];

    if (crossesAntimeridian(prev.lon, curr.lon)) {
      // Calculate where the line crosses the antimeridian
      const crossingLat = calculateCrossingLatitude(
        prev.lat,
        prev.lon,
        curr.lat,
        curr.lon
      );

      // End current segment at the antimeridian
      const exitLon = prev.lon > 0 ? 180 : -180;
      currentSegment.push({ lat: crossingLat, lon: exitLon });
      segments.push(currentSegment);

      // Start new segment from the other side of the antimeridian
      const entryLon = prev.lon > 0 ? -180 : 180;
      currentSegment = [{ lat: crossingLat, lon: entryLon }, curr];
    } else {
      currentSegment.push(curr);
    }
  }

  // Don't forget the last segment
  if (currentSegment.length > 0) {
    segments.push(currentSegment);
  }

  return segments;
}

/**
 * Converts split segments to GeoJSON MultiLineString format.
 * Use this for Mapbox GL JS.
 */
export function toMultiLineString(segments: Coordinate[][]): GeoJSON.MultiLineString {
  return {
    type: 'MultiLineString',
    coordinates: segments.map(segment =>
      segment.map(coord => [coord.lon, coord.lat])
    )
  };
}
